import { resolveMainComputationSnapshot } from "../../../MainTaxComputation/utils/resolveMainComputationSnapshot";
import { buildReview } from "./review";
import { basisPeriod, computationPeriod, dateOnly } from "./preparation";
import { createGroupReliefOptimiser } from "./optimiser";
import { calculateGroupReliefOutput } from "./groupReliefComputation";

const keys = ["caTransfer", "lossesTransfer", "donationsTransfer"];
export const optimisationInputKey = (selected, settings, confirmed) =>
  JSON.stringify({ selected, settings, confirmed });

export function runOptimisation(selected, settings, eligibilityConfirmed) {
  const review = buildReview(selected, settings);
  if (review.issues.length) throw new Error(review.issues[0].message);
  if (!eligibilityConfirmed)
    throw new Error(
      "Confirm eligibility for all selected company pairs before optimisation.",
    );
  const basis = basisPeriod(settings.commonFinancialYearEnd);
  if (!basis) throw new Error("Choose a valid case basis period.");
  const companies = review.companies.map((company, index) => {
    const draft = selected[index];
    const computation = {
      ...draft.computation,
      financialYearStart: dateOnly(draft.computation.financialYearStart),
      financialYearEnd: dateOnly(draft.computation.financialYearEnd),
      basisPeriods: (draft.computation.basisPeriods || []).map((period) => ({
        ...period,
        financialYearStart: dateOnly(period.financialYearStart),
        financialYearEnd: dateOnly(period.financialYearEnd),
      })),
    };
    const resolution = resolveMainComputationSnapshot({
      computation,
      doc: draft.sourceDoc,
      settings,
    });
    const actualDays =
      resolution.groupRelief?.basisDays || computationPeriod(computation)?.days;
    if (!actualDays || !resolution.groupRelief)
      throw new Error(
        `${company.name}: the current YA basis period is unavailable.`,
      );
    const days = Math.min(
      basis.days,
      actualDays,
      company.continuousQualifyingDays,
    );
    if (!Number.isSafeInteger(days) || days < 1)
      throw new Error(`${company.name}: check continuous qualifying days.`);
    if (
      resolution.companyType === "investment" &&
      (draft.sourceDoc.referenceData?.currentYearCapitalAllowances || []).some(
        (row) => Number(row.amount || row.current_amount || 0) !== 0,
      )
    ) {
      throw new Error(
        `${company.name}: investment-company allowances need category verification before automated group relief. Review the source computation.`,
      );
    }
    return {
      ...company,
      computation,
      sourceDoc: draft.sourceDoc,
      resolution,
      shareholdingMaintainedThroughout: days === basis.days,
      continuousQualifyingDays: days,
    };
  });
  const calculateCompanyComputation = (
    company,
    caseSettings,
    adjustment = {},
  ) => {
    const claimed = keys.reduce(
      (total, key) => total + Math.max(0, -(adjustment[key] || 0)),
      0,
    );
    const resolution = claimed
      ? resolveMainComputationSnapshot({
          computation: company.computation,
          doc: company.sourceDoc,
          settings: caseSettings,
          groupReliefClaimSgd: claimed,
        })
      : company.resolution;
    const current = resolution.periods.find(
      (period) => period.key === "current",
    );
    const ownCurrent = company.resolution.periods.find(
      (period) => period.key === "current",
    );
    const group = company.resolution.groupRelief;
    const detailed = group.detailedOutput || {};
    const { statutoryIncomeLessLosses, assessableIncome, chargeableIncome } =
      calculateGroupReliefOutput(detailed, adjustment);
    // Start with the shared computation after all own losses and donations,
    // before exemption. Every claim consumes this budget; outgoing transfers
    // cannot create a new budget for receiving Group Relief.
    const claimCapacity = Math.max(
      0,
      Number(ownCurrent?.chargeableIncome) - claimed,
    );
    const amounts = [
      current?.taxPayable,
      current?.taxPayableBeforeEci,
      current?.chargeableIncome,
      ownCurrent?.chargeableIncome,
      group.assessableIncome,
      assessableIncome,
      chargeableIncome,
      claimCapacity,
      ...Object.values(group.utilisation),
    ];
    if (amounts.some((amount) => !Number.isFinite(Number(amount))))
      throw new Error(`${company.name}: invalid computation amounts.`);
    return {
      statutoryIncomeLessLosses,
      assessableIncome,
      claimCapacity,
      utilisation: group.utilisation,
      taxPayable: Number(current.taxPayable),
      taxPayableBeforeEci: Number(current.taxPayableBeforeEci),
      chargeableIncome: Number(current.chargeableIncome),
      taxExemption: Number(current.taxExemption || 0),
      taxBeforeRebate: Number(current.taxBeforeRebate || 0),
      citRebate: Number(current.citRebate || 0),
      claimed,
      resolution,
    };
  };
  const summariseComputations = (rows) => ({
    taxPayable:
      Math.round(rows.reduce((sum, row) => sum + row.taxPayable, 0) * 100) /
      100,
    taxPayableBeforeEci:
      Math.round(
        rows.reduce((sum, row) => sum + row.taxPayableBeforeEci, 0) * 100,
      ) / 100,
  });
  const { optimiseGroupRelief } = createGroupReliefOptimiser({
    calculateCompanyComputation,
    summariseComputations,
  });
  const result = optimiseGroupRelief(companies, settings, basis.days);
  result.taxSaving = Math.round(result.taxSaving * 100) / 100;
  result.taxSavingBeforeEci =
    Math.round(
      (result.beforeSummary.taxPayableBeforeEci -
        result.afterSummary.taxPayableBeforeEci) *
        100,
    ) / 100;
  if (!result.reconciliation.balanced || result.taxSaving < -0.01)
    throw new Error(
      "The result did not pass reconciliation. Review the source computations.",
    );
  result.generatedAt = new Date().toISOString();
  result.companies = result.companies.map(
    ({ sourceDoc, ...company }) => company,
  );
  return result;
}
